// ブラウザ完結版のユーザー認証（Supabase Auth）。
// 設計根拠: docs/Supabase認証ユーザ登録機能.md
//  - 恒久ユーザー ID は auth.users.id（メールアドレスを識別子にしない）
//  - トークンは supabase-js の管理へ一任（localStorage）。独自複製はしない
//  - エラーは日本語の安全な文言へ変換して表示（内部エラー・メール存在有無を露出しない）
//  - 接続先（URL / publishable key）はビルド時に esbuild define で注入する。
//    未注入時は enabled=false となり、UI は認証なしで従来どおり動作する（ローカル開発）。
import { createClient } from '@supabase/supabase-js';

/* global __VN_SUPABASE_URL__, __VN_SUPABASE_PUBLISHABLE_KEY__ */
const SUPABASE_URL = typeof __VN_SUPABASE_URL__ !== 'undefined' ? __VN_SUPABASE_URL__ : '';
const SUPABASE_KEY = typeof __VN_SUPABASE_PUBLISHABLE_KEY__ !== 'undefined' ? __VN_SUPABASE_PUBLISHABLE_KEY__ : '';

// ── 純関数（テスト対象）─────────────────────────────

/** ログイン後に復帰してよいのはツール内のハッシュルートのみ（Open Redirect 防止）。 */
export function sanitizeReturnHash(hash) {
    if (typeof hash !== 'string') return '#/';
    const value = hash.trim();
    // '#/xxx' 形式のみ許可。'//', 'http:', 'javascript:' 等の混入は既定へ落とす
    if (!/^#\/[A-Za-z0-9\-_/?=&.]*$/.test(value)) return '#/';
    // 認証系画面への復帰はループの元なので既定へ
    if (isAuthRoute(value)) return '#/';
    return value;
}

/** 未ログインでも表示してよい画面（認証系のみ。ツール本体は全て要ログイン）。 */
export function isAuthRoute(hash) {
    const base = String(hash ?? '').split('?')[0];
    return ['#/login', '#/signup', '#/forgot-password', '#/reset-password', '#/auth/callback'].includes(base);
}

/** Supabase のエラーを利用者向けの安全な日本語文言へ変換する。
 *  メールアドレスの存在有無・プロバイダ種別・内部メッセージは露出しない。 */
export function translateAuthError(error, fallback = '処理を完了できませんでした。しばらくしてから再度お試しください。') {
    const raw = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
    const status = Number(error?.status ?? 0);
    if (/invalid[_ ]credentials|invalid login credentials/.test(raw)) {
        return 'ログインに失敗しました。メールアドレスまたはパスワードを確認してください。';
    }
    if (/email[_ ]not[_ ]confirmed/.test(raw)) {
        return 'メールアドレスの確認が完了していません。登録時に届いた確認メールのリンクを開いてください。';
    }
    if (/weak[_ ]password|password should be at least|password.*length/.test(raw)) {
        return 'パスワードは 8文字以上で入力してください。';
    }
    if (/user[_ ]already[_ ]exists|already registered/.test(raw)) {
        // メール列挙対策: 登録済みとは明言しない
        return '登録処理を完了できませんでした。入力内容を確認して、しばらくしてから再度お試しください。';
    }
    if (/rate[_ ]limit|too many requests/.test(raw) || status === 429) {
        return '操作が続けて行われたため制限されています。しばらく待ってから再度お試しください。';
    }
    if (/expired|invalid.*token|otp[_ ]expired|flow[_ ]state/.test(raw)) {
        return '認証リンクが無効または期限切れです。もう一度最初から操作してください。';
    }
    if (/session[_ ]not[_ ]found|refresh[_ ]token/.test(raw)) {
        return 'セッションの有効期限が切れました。もう一度ログインしてください。';
    }
    if (/fetch|network|failed to fetch/.test(raw)) {
        return '通信に失敗しました。ネットワーク接続を確認して再度お試しください。';
    }
    return fallback;
}

/** パスワードの事前検証（Supabase 側設定=最小8文字と表示を一致させる）。 */
export function validatePassword(password, confirm) {
    if (typeof password !== 'string' || password.length < 8) {
        return 'パスワードは 8文字以上で入力してください。';
    }
    if (password.length > 72) {
        return 'パスワードが長すぎます（72文字以内）。';
    }
    if (confirm !== undefined && password !== confirm) {
        return 'パスワード（確認）が一致しません。';
    }
    return null;
}

/** メールアドレスの事前検証（詳細な形式判定はサーバ側に委ねる）。 */
export function validateEmail(email) {
    const value = String(email ?? '').trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value.length > 255) {
        return 'メールアドレスの形式を確認してください。';
    }
    return null;
}

// ── 認証クライアント ────────────────────────────────

export function createAuth() {
    if (!SUPABASE_URL || !SUPABASE_KEY) return null; // 未設定＝認証なしで動作（ローカル開発）
    const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
        auth: {
            flowType: 'pkce',
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true, // メール確認・OAuth 復帰の ?code= を自動交換
        },
    });

    const state = {
        user: null,
        session: null,
        profile: null,
        isLoading: true,
        passwordRecovery: false, // 再設定リンク経由のセッションか
    };
    const listeners = new Set();
    const notify = () => { for (const cb of listeners) cb(state); };

    async function loadProfile() {
        if (!state.user) { state.profile = null; return; }
        const { data, error } = await supabase.from('profiles').select('*').eq('id', state.user.id).maybeSingle();
        state.profile = error ? null : data;
    }

    function setSession(session) {
        state.session = session ?? null;
        state.user = session?.user ?? null;
    }

    async function init() {
        // リスナーは getSession より先に登録する。PKCE の URL 交換（メール確認・OAuth・再設定リンク）
        // は初期化時に走るため、後から登録すると PASSWORD_RECOVERY イベントを取りこぼす
        supabase.auth.onAuthStateChange((event, session) => {
            setSession(session);
            if (event === 'PASSWORD_RECOVERY') state.passwordRecovery = true;
            if (event === 'SIGNED_OUT') state.passwordRecovery = false;
            // プロフィール取得は非同期で追随（await しない: リスナー内での deadlock 回避）
            loadProfile().finally(notify);
            notify();
        });
        const { data } = await supabase.auth.getSession();
        setSession(data?.session);
        await loadProfile();
        state.isLoading = false;
        notify();
        return state;
    }

    return {
        enabled: true,
        state,
        init,
        onChange(cb) { listeners.add(cb); return () => listeners.delete(cb); },
        isAuthenticated: () => !!state.session,

        async signUp(email, password) {
            const { error } = await supabase.auth.signUp({
                email: String(email).trim(),
                password,
                // 復帰先は常にルート（/en /ja で起動していても Supabase の許可 URL に一致させる。言語は localStorage が保持）
                options: { emailRedirectTo: `${location.origin}/` },
            });
            if (error) return { ok: false, error: translateAuthError(error) };
            return { ok: true, message: '確認メールを送信しました。メール内のリンクを開いて登録を完了してください。' };
        },

        async signIn(email, password) {
            const { error } = await supabase.auth.signInWithPassword({ email: String(email).trim(), password });
            if (error) return { ok: false, error: translateAuthError(error) };
            return { ok: true };
        },

        async signInWithOAuth(provider) {
            if (provider !== 'google' && provider !== 'apple') return { ok: false, error: '不明なログイン方法です。' };
            const { error } = await supabase.auth.signInWithOAuth({
                provider,
                options: { redirectTo: `${location.origin}/` },
            });
            if (error) {
                const label = provider === 'google' ? 'Google' : 'Apple';
                return { ok: false, error: `${label} ログインを完了できませんでした。しばらくしてから再度お試しください。` };
            }
            return { ok: true, redirecting: true }; // このあとブラウザが外部認証へ遷移する
        },

        async signOut() {
            try { await supabase.auth.signOut(); } catch { /* 失敗してもローカル状態は下で必ずクリア */ }
            setSession(null);
            state.profile = null;
            state.passwordRecovery = false;
            notify();
            return { ok: true };
        },

        async sendPasswordReset(email) {
            const { error } = await supabase.auth.resetPasswordForEmail(String(email).trim(), {
                redirectTo: `${location.origin}/`,
            });
            if (error && Number(error.status) === 429) return { ok: false, error: translateAuthError(error) };
            // メール存在有無を露出しない: 成功・未登録どちらも同じ完了表示にする
            return { ok: true, message: '入力されたメールアドレス宛に、パスワード再設定の案内を送信しました（登録がある場合）。' };
        },

        async updatePassword(newPassword) {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) return { ok: false, error: translateAuthError(error) };
            state.passwordRecovery = false;
            return { ok: true, message: 'パスワードを更新しました。' };
        },

        async updateProfile({ displayName, avatarUrl } = {}) {
            if (!state.user) return { ok: false, error: 'ログインが必要です。' };
            const patch = {};
            if (displayName !== undefined) {
                const name = String(displayName).trim();
                if (name === '' || name.length > 60) return { ok: false, error: '表示名は 1〜60文字で入力してください。' };
                patch.display_name = name;
            }
            if (avatarUrl !== undefined) {
                const url = String(avatarUrl).trim();
                if (url !== '' && !/^https:\/\//.test(url)) return { ok: false, error: 'アバター URL は https:// で始まる必要があります。' };
                if (url.length > 2048) return { ok: false, error: 'アバター URL が長すぎます。' };
                patch.avatar_url = url === '' ? null : url;
            }
            if (Object.keys(patch).length === 0) return { ok: false, error: '変更内容がありません。' };
            const { error } = await supabase.from('profiles').update(patch).eq('id', state.user.id);
            if (error) return { ok: false, error: translateAuthError(error) };
            await loadProfile();
            notify();
            return { ok: true, message: 'プロフィールを保存しました。' };
        },

        async refreshProfile() { await loadProfile(); notify(); },
    };
}
